/**
 * ResumeWorkWidget — Dashboard widget for resuming previous work.
 * 
 * Shows:
 *  - Recent activity history
 *  - Draft documents
 *  - Favorite/pinned menus
 * 
 * User MANUALLY chooses to continue — NEVER auto-opens.
 */
import React, { useState, useEffect } from 'react';
import { 
  History, FileText, Star, ChevronRight, Clock, X,
  File, Edit, Eye, Trash2, Pin, RefreshCw
} from 'lucide-react';
import SessionService from '@/services/SessionService';

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVITY ITEM
// ═══════════════════════════════════════════════════════════════════════════
const ActivityItem = ({ activity, onResume, onRemove, isDark }) => {
  const timeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg group transition-colors ${
      isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
    }`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-500'
      }`}>
        {activity.type === 'screen' && <Edit size={14} />}
        {activity.type === 'report' && <FileText size={14} />}
        {activity.type === 'transaction' && <File size={14} />}
        {!activity.type && <History size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          {activity.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {activity.module}
          </span>
          <span className={`text-[10px] flex items-center gap-0.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            <Clock size={8} />
            {timeAgo(activity.timestamp)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onResume(activity)}
          className={`p-1.5 rounded-lg transition-colors ${
            isDark ? 'hover:bg-blue-500/20 text-blue-400' : 'hover:bg-blue-100 text-blue-500'
          }`}
          title="Resume"
        >
          <ChevronRight size={14} />
        </button>
        <button
          onClick={() => onRemove(activity.id)}
          className={`p-1.5 rounded-lg transition-colors ${
            isDark ? 'hover:bg-red-500/20 text-slate-600 hover:text-red-400' : 'hover:bg-red-100 text-slate-400 hover:text-red-500'
          }`}
          title="Remove"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DRAFT ITEM
// ═══════════════════════════════════════════════════════════════════════════
const DraftItem = ({ draft, onResume, onDelete, isDark }) => (
  <div className={`p-3 rounded-lg border transition-colors ${
    isDark ? 'bg-[#151515] border-[#333] hover:border-[#444]' : 'bg-white border-slate-200 hover:border-slate-300'
  }`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          {draft.summary}
        </p>
        <p className={`text-[10px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          {draft.screenId} • Saved {new Date(draft.timestamp).toLocaleDateString()}
        </p>
      </div>
    </div>
    <div className="flex gap-1.5 mt-2">
      <button
        onClick={() => onResume(draft)}
        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase rounded bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
      >
        <RefreshCw size={10} />
        Continue
      </button>
      <button
        onClick={() => onDelete(draft.screenId)}
        className={`px-2 py-1.5 rounded transition-colors ${
          isDark ? 'bg-[#222] text-slate-500 hover:text-red-400' : 'bg-slate-100 text-slate-400 hover:text-red-500'
        }`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// PINNED ITEM
// ═══════════════════════════════════════════════════════════════════════════
const PinnedItem = ({ item, onNavigate, onUnpin, isDark }) => (
  <div className={`flex items-center gap-2 p-2 rounded-lg group transition-colors cursor-pointer ${
    isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
  }`}
    onClick={() => onNavigate(item.moduleId)}
  >
    <Star size={12} className="text-amber-500 shrink-0" />
    <span className={`flex-1 text-xs font-medium truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
      {item.label}
    </span>
    <button
      onClick={(e) => { e.stopPropagation(); onUnpin(item.moduleId); }}
      className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${
        isDark ? 'hover:bg-white/10 text-slate-600' : 'hover:bg-slate-200 text-slate-400'
      }`}
      title="Unpin"
    >
      <X size={10} />
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN WIDGET
// ═══════════════════════════════════════════════════════════════════════════
export default function ResumeWorkWidget({ 
  theme = 'light', 
  onNavigate,
  className = ''
}) {
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('recent'); // recent | drafts | pinned
  const [recentActivities, setRecentActivities] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [pinnedModules, setPinnedModules] = useState([]);

  // Load data
  useEffect(() => {
    setRecentActivities(SessionService.getRecentActivities());
    setDrafts(SessionService.getDraftReferences());
    setPinnedModules(SessionService.getPinnedModules());
  }, []);

  const handleResumeActivity = (activity) => {
    if (onNavigate && activity.moduleId) {
      onNavigate(activity.moduleId, activity.subItemId);
    }
  };

  const handleRemoveActivity = (id) => {
    setRecentActivities(prev => {
      const updated = prev.filter(a => a.id !== id);
      localStorage.setItem('retailcliq_recent_activity', JSON.stringify(updated));
      return updated;
    });
  };

  const handleResumeDraft = (draft) => {
    if (onNavigate && draft.screenId) {
      onNavigate(draft.screenId);
    }
  };

  const handleDeleteDraft = (screenId) => {
    SessionService.clearDraftReference(screenId);
    setDrafts(SessionService.getDraftReferences());
  };

  const handleUnpin = (moduleId) => {
    const updated = pinnedModules.filter(p => p.moduleId !== moduleId);
    SessionService.savePinnedModules(updated);
    setPinnedModules(updated);
  };

  const totalItems = recentActivities.length + drafts.length + pinnedModules.length;
  
  // Don't render if nothing to show
  if (totalItems === 0) {
    return null;
  }

  return (
    <div 
      data-testid="resume-work-widget"
      className={`rounded-xl border ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200 shadow-sm'} ${className}`}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-xs font-black uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Resume Previous Work
          </h3>
          <div className={`px-2 py-0.5 rounded text-[9px] font-bold ${
            isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'
          }`}>
            {totalItems} items
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
        {[
          { id: 'recent', label: 'Recent', icon: History, count: recentActivities.length },
          { id: 'drafts', label: 'Drafts', icon: FileText, count: drafts.length },
          { id: 'pinned', label: 'Pinned', icon: Star, count: pinnedModules.length },
        ].filter(tab => tab.count > 0).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase transition-colors ${
              activeTab === tab.id
                ? isDark ? 'text-white border-b-2 border-blue-500 -mb-px' : 'text-blue-600 border-b-2 border-blue-500 -mb-px'
                : isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <tab.icon size={12} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1 py-px rounded text-[8px] ${
                isDark ? 'bg-white/10' : 'bg-slate-100'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-3 max-h-[300px] overflow-y-auto">
        {/* Recent Activities */}
        {activeTab === 'recent' && (
          <div className="space-y-1">
            {recentActivities.length === 0 ? (
              <p className={`text-xs text-center py-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                No recent activity
              </p>
            ) : (
              recentActivities.map(activity => (
                <ActivityItem
                  key={activity.id || activity.timestamp}
                  activity={activity}
                  onResume={handleResumeActivity}
                  onRemove={handleRemoveActivity}
                  isDark={isDark}
                />
              ))
            )}
          </div>
        )}

        {/* Drafts */}
        {activeTab === 'drafts' && (
          <div className="space-y-2">
            {drafts.length === 0 ? (
              <p className={`text-xs text-center py-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                No saved drafts
              </p>
            ) : (
              drafts.map(draft => (
                <DraftItem
                  key={draft.screenId}
                  draft={draft}
                  onResume={handleResumeDraft}
                  onDelete={handleDeleteDraft}
                  isDark={isDark}
                />
              ))
            )}
          </div>
        )}

        {/* Pinned Modules */}
        {activeTab === 'pinned' && (
          <div className="space-y-1">
            {pinnedModules.length === 0 ? (
              <p className={`text-xs text-center py-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                No pinned modules
              </p>
            ) : (
              pinnedModules.map(item => (
                <PinnedItem
                  key={item.moduleId}
                  item={item}
                  onNavigate={onNavigate}
                  onUnpin={handleUnpin}
                  isDark={isDark}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
